/**
 * Bundle builder — universal search + custom client-side pagination.
 * Loads all products once (high app Items Per Page); theme paginates in the browser.
 */
(() => {
  const config = window.EarthHeroBundleConfig || {};
  const filterEnabled = config.searchEnabled !== false;
  const itemsPerPage = Math.max(1, parseInt(config.itemsPerPage, 10) || 24);

  const CARD_SELECTORS = '.bdlr__product-card, .enoble_bundle-item, .bdlr__products--container .product-card';

  let widgetRoot = null;
  let searchInput = null;
  let clearBtn = null;
  let emptyEl = null;
  let searchRoot = null;
  let paginationRoot = null;
  let paginationStatus = null;
  let currentQuery = '';
  let lastAppliedQuery = null;
  let currentPage = 1;
  let debounceTimer = null;
  let ensureTimer = null;
  let cardsCache = [];
  const cardTextCache = new WeakMap();

  const getWidgetRoot = () => {
    widgetRoot =
      document.querySelector('.bundable-widget-container') ||
      document.querySelector('.bdlr__bundle-widget--container') ||
      null;
    if (!widgetRoot) {
      const block = document.querySelector('.bundle-builder-apps .shopify-app-block');
      widgetRoot = block?.querySelector('.bundable-widget-container, .bdlr__bundle-widget--container') || block;
    }
    return widgetRoot;
  };

  const getProductsContainer = (root) =>
    root?.querySelector('.bdlr__products--container, .bdlr__products-grid, .bdlr__products');

  const isProductsReady = (root) => {
    if (!root) return false;
    if (root.querySelector('.bdlr__loading')) return false;
    return root.querySelector(CARD_SELECTORS) !== null;
  };

  const isSearchInHeader = () => {
    const title = getWidgetRoot()?.querySelector('.bdlr__title');
    if (!searchRoot?.isConnected || !title) return false;
    const header = title.closest('.earth-hero-bundle-header');
    return header?.contains(searchRoot) === true;
  };

  const bindElements = () => {
    const domSearch = document.getElementById('earth-hero-bundle-search');
    const domInput = document.getElementById('earth-hero-bundle-search-input');
    const domEmpty = document.getElementById('earth-hero-bundle-search-empty');

    if (domSearch) searchRoot = domSearch;
    if (domInput) searchInput = domInput;
    if (domEmpty) emptyEl = domEmpty;

    clearBtn = searchRoot?.querySelector('.earth-hero-bundle-search__clear') || clearBtn;
    return !!(searchRoot && searchInput);
  };

  const restoreSearchToWrap = () => {
    const wrap = document.querySelector('.earth-hero-bundle-search-wrap');
    if (!wrap || !bindElements()) return;

    if (!searchRoot.isConnected) {
      wrap.appendChild(searchRoot);
    }
    if (emptyEl && !emptyEl.isConnected) {
      wrap.appendChild(emptyEl);
    }
    wrap.classList.remove('earth-hero-bundle-search-wrap--placed');
  };

  const placeSearchInTitle = () => {
    if (!bindElements()) return false;

    const root = getWidgetRoot();
    const title = root?.querySelector('.bdlr__title');
    if (!title?.parentElement) return false;

    restoreSearchToWrap();

    let header = title.closest('.earth-hero-bundle-header');
    if (!header) {
      header = document.createElement('div');
      header.className = 'earth-hero-bundle-header';
      title.parentElement.insertBefore(header, title);
      header.appendChild(title);
    }

    if (!header.contains(searchRoot)) {
      header.appendChild(searchRoot);
    }

    if (emptyEl) {
      const mount = header.parentElement;
      if (mount && !mount.contains(emptyEl)) {
        header.insertAdjacentElement('afterend', emptyEl);
      }
    }

    document.querySelector('.earth-hero-bundle-search-wrap')?.classList.add('earth-hero-bundle-search-wrap--placed');
    return true;
  };

  const ensurePaginationMount = () => {
    const root = getWidgetRoot();
    const container = getProductsContainer(root);
    if (!container) return null;

    if (!paginationRoot || !paginationRoot.isConnected) {
      paginationRoot = document.getElementById('earth-hero-bundle-pagination');
      if (!paginationRoot) {
        paginationRoot = document.createElement('div');
        paginationRoot.id = 'earth-hero-bundle-pagination';
        paginationRoot.className = 'earth-hero-bundle-pagination';
        paginationRoot.hidden = true;

        paginationStatus = document.createElement('p');
        paginationStatus.className = 'earth-hero-bundle-pagination__status';
        paginationStatus.setAttribute('aria-live', 'polite');
        paginationRoot.appendChild(paginationStatus);

        const wrapper = document.createElement('div');
        wrapper.className = 'pagination-wrapper';
        wrapper.innerHTML =
          '<nav class="pagination" role="navigation" aria-label="Bundle products pagination"><ul class="pagination__list list-unstyled" role="list" data-earth-hero-pagination-list></ul></nav>';
        paginationRoot.appendChild(wrapper);
      }
    }

    if (!container.contains(paginationRoot)) {
      container.insertAdjacentElement('afterend', paginationRoot);
    }

    if (!paginationStatus) {
      paginationStatus = paginationRoot.querySelector('.earth-hero-bundle-pagination__status');
    }

    return paginationRoot;
  };

  const showSearch = () => {
    if (!bindElements()) return false;

    searchRoot.hidden = false;
    searchRoot.classList.remove('earth-hero-bundle-search--pending');
    searchRoot.classList.add('earth-hero-bundle-search--ready');
    document.querySelector('.earth-hero-bundle-header')?.classList.add('earth-hero-bundle-header--ready');
    document.body.classList.add('bundle-builder-search-ready');
    return true;
  };

  const hideSearch = () => {
    if (!searchRoot) return;
    searchRoot.hidden = true;
    searchRoot.classList.add('earth-hero-bundle-search--pending');
    searchRoot.classList.remove('earth-hero-bundle-search--ready');
  };

  const refreshCardsCache = () => {
    const root = getWidgetRoot();
    cardsCache = root ? [...root.querySelectorAll(CARD_SELECTORS)] : [];
  };

  const getCardSearchText = (card) => {
    if (cardTextCache.has(card)) return cardTextCache.get(card);

    const title =
      card.querySelector('.bdlr__card-title, .bdlr__product--title h4, .bdlr__product--title')?.textContent?.trim() ||
      '';
    const meta = card.querySelector('.bdlr__product--meta, .bdlr__card-price')?.textContent?.trim() || '';
    const text = `${title} ${meta}`.toLowerCase().replace(/\s+/g, ' ').trim();

    cardTextCache.set(card, text);
    return text;
  };

  const updateClearButton = () => {
    if (!clearBtn || !searchInput) return;
    clearBtn.hidden = searchInput.value.length === 0;
  };

  const scrollToProducts = () => {
    const anchor =
      document.querySelector('.earth-hero-bundle-header') ||
      getProductsContainer(getWidgetRoot()) ||
      getWidgetRoot();
    if (!anchor) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      return;
    }
    const top = anchor.getBoundingClientRect().top + window.scrollY - 24;
    window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'smooth' });
  };

  const getMatchingCards = (query) => {
    if (!query) return cardsCache;
    return cardsCache.filter((card) => getCardSearchText(card).includes(query));
  };

  const getPageParts = (current, total) => {
    if (total <= 1) return [{ type: 'page', value: 1 }];
    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => ({ type: 'page', value: index + 1 }));
    }

    const parts = [{ type: 'page', value: 1 }];
    const left = Math.max(2, current - 1);
    const right = Math.min(total - 1, current + 1);

    if (left > 2) parts.push({ type: 'ellipsis' });
    for (let page = left; page <= right; page += 1) {
      parts.push({ type: 'page', value: page });
    }
    if (right < total - 1) parts.push({ type: 'ellipsis' });
    parts.push({ type: 'page', value: total });
    return parts;
  };

  const arrowSvg = (paths) =>
    `<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">${paths}</svg>`;

  const renderPagination = (totalPages, totalMatches) => {
    ensurePaginationMount();
    if (!paginationRoot) return;

    if (totalPages <= 1) {
      paginationRoot.hidden = true;
      if (paginationStatus) paginationStatus.textContent = '';
      return;
    }

    paginationRoot.hidden = false;

    if (paginationStatus) {
      const start = (currentPage - 1) * itemsPerPage + 1;
      const end = Math.min(currentPage * itemsPerPage, totalMatches);
      paginationStatus.textContent = `Showing ${start}–${end} of ${totalMatches} products`;
    }

    const list = paginationRoot.querySelector('[data-earth-hero-pagination-list]');
    if (!list) return;

    const firstDisabled = currentPage <= 1;
    const prevDisabled = currentPage <= 1;
    const nextDisabled = currentPage >= totalPages;
    const lastDisabled = currentPage >= totalPages;
    const parts = getPageParts(currentPage, totalPages);

    const firstPaths =
      '<path d="M15.3913 15.75L16.625 14.5163L12.6175 10.5L16.625 6.48375L15.3912 5.25L10.1412 10.5L15.3913 15.75Z" fill="#122145"/><path d="M9.62489 15.75L10.8586 14.5163L6.85114 10.5L10.8586 6.48375L9.62489 5.25L4.37489 10.5L9.62489 15.75Z" fill="#122145"/>';
    const prevPaths =
      '<path d="M13.2038 15.75L14.4375 14.5163L10.43 10.5L14.4375 6.48375L13.2037 5.25L7.95375 10.5L13.2038 15.75Z" fill="#122145"/>';
    const nextPaths =
      '<path d="M9.10875 5.25L7.875 6.48375L11.8825 10.5L7.875 14.5163L9.10875 15.75L14.3587 10.5L9.10875 5.25Z" fill="#122145"/>';
    const lastPaths =
      '<path d="M5.60875 5.25L4.375 6.48375L8.3825 10.5L4.375 14.5163L5.60875 15.75L10.8587 10.5L5.60875 5.25Z" fill="#122145"/><path d="M11.3751 5.25L10.1414 6.48375L14.1489 10.5L10.1414 14.5163L11.3751 15.75L16.6251 10.5L11.3751 5.25Z" fill="#122145"/>';

    const navButton = (action, label, disabled, paths) => {
      if (disabled) {
        return `<li><span class="pagination__item pagination__item-arrow pagination__item--disabled" aria-hidden="true">${arrowSvg(paths)}</span></li>`;
      }
      return `<li><button type="button" class="pagination__item pagination__item-arrow link motion-reduce" data-earth-hero-page="${action}" aria-label="${label}">${arrowSvg(paths)}</button></li>`;
    };

    let html = '';
    html += navButton('first', 'First page', firstDisabled, firstPaths);
    html += navButton('prev', 'Previous page', prevDisabled, prevPaths);

    parts.forEach((part) => {
      if (part.type === 'ellipsis') {
        html += '<li><span class="pagination__item">…</span></li>';
        return;
      }
      if (part.value === currentPage) {
        html += `<li><span role="link" aria-disabled="true" class="pagination__item pagination__item--current" aria-current="page" aria-label="Page ${part.value}">${part.value}</span></li>`;
        return;
      }
      html += `<li><button type="button" class="pagination__item link" data-earth-hero-page="${part.value}" aria-label="Page ${part.value}">${part.value}</button></li>`;
    });

    html += navButton('next', 'Next page', nextDisabled, nextPaths);
    html += navButton('last', 'Last page', lastDisabled, lastPaths);

    list.innerHTML = html;
  };

  const goToPage = (page, totalPages, scroll = true) => {
    const nextPage = Math.min(Math.max(1, page), totalPages);
    if (nextPage === currentPage) return;
    currentPage = nextPage;
    lastAppliedQuery = null;
    applyFilter({ scroll });
  };

  const bindPaginationListeners = () => {
    if (!paginationRoot || paginationRoot.dataset.earthHeroPaginationBound === 'true') return;

    paginationRoot.addEventListener('click', (event) => {
      const button = event.target.closest('[data-earth-hero-page]');
      if (!button) return;
      event.preventDefault();

      const query = currentQuery.trim().toLowerCase();
      const matching = getMatchingCards(query);
      const totalPages = Math.max(1, Math.ceil(matching.length / itemsPerPage));
      const action = button.getAttribute('data-earth-hero-page');

      if (action === 'first') goToPage(1, totalPages);
      else if (action === 'prev') goToPage(currentPage - 1, totalPages);
      else if (action === 'next') goToPage(currentPage + 1, totalPages);
      else if (action === 'last') goToPage(totalPages, totalPages);
      else goToPage(parseInt(action, 10), totalPages);
    });

    paginationRoot.dataset.earthHeroPaginationBound = 'true';
  };

  const getActiveQuery = () => {
    if (!filterEnabled || !searchRoot || searchRoot.hidden) return '';
    return currentQuery.trim().toLowerCase();
  };

  const applyFilter = ({ scroll = false } = {}) => {
    const root = getWidgetRoot();
    if (!isProductsReady(root)) return;

    const query = getActiveQuery();
    const stateKey = `${query}::${currentPage}`;
    if (stateKey === lastAppliedQuery && !scroll) return;
    lastAppliedQuery = stateKey;

    if (!cardsCache.length || !cardsCache[0]?.isConnected) {
      refreshCardsCache();
    }

    const matching = getMatchingCards(query);
    const totalPages = Math.max(1, Math.ceil(matching.length / itemsPerPage));
    if (currentPage > totalPages) currentPage = totalPages;

    const visibleSet = new Set(
      matching.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    );

    cardsCache.forEach((card) => {
      const matches = !query || getCardSearchText(card).includes(query);
      const onPage = visibleSet.has(card);
      card.classList.toggle('earth-hero-bdlr-search-hidden', !matches || !onPage);
    });

    if (emptyEl) {
      if (query && matching.length === 0) {
        emptyEl.textContent = `No matching products for "${currentQuery.trim()}".`;
        emptyEl.hidden = false;
      } else {
        emptyEl.hidden = true;
        emptyEl.textContent = '';
      }
    }

    ensurePaginationMount();
    bindPaginationListeners();
    renderPagination(totalPages, matching.length);

    if (scroll) scrollToProducts();
  };

  const reapplyAfterProductsUpdate = () => {
    if (searchInput) currentQuery = searchInput.value;
    refreshCardsCache();
    lastAppliedQuery = null;
    applyFilter();
  };

  const onInput = () => {
    if (!searchInput) return;
    currentQuery = searchInput.value;
    currentPage = 1;
    updateClearButton();
    lastAppliedQuery = null;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => applyFilter({ scroll: false }), 280);
  };

  const clearSearch = () => {
    currentQuery = '';
    currentPage = 1;
    lastAppliedQuery = null;
    if (searchInput) searchInput.value = '';
    updateClearButton();
    applyFilter({ scroll: false });
    searchInput?.focus({ preventScroll: true });
  };

  const bindListeners = () => {
    if (!searchInput || searchInput.dataset.earthHeroSearchBound === 'true') return;

    searchInput.addEventListener('input', onInput, { passive: true });
    searchInput.addEventListener('search', () => {
      if (!searchInput.value) clearSearch();
      else onInput();
    });
    searchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        clearSearch();
      }
    });
    clearBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      clearSearch();
    });

    searchInput.dataset.earthHeroSearchBound = 'true';
  };

  const ensureSearch = () => {
    if (!bindElements()) return;

    const root = getWidgetRoot();
    if (!isProductsReady(root)) {
      hideSearch();
      return;
    }

    const needsMount = !isSearchInHeader() || searchRoot.hidden;

    if (needsMount) {
      placeSearchInTitle();
      showSearch();
      bindListeners();
      updateClearButton();
      refreshCardsCache();
      ensurePaginationMount();
      bindPaginationListeners();
      if (currentQuery || searchInput?.value) {
        currentQuery = searchInput?.value || currentQuery;
        lastAppliedQuery = null;
        applyFilter();
      } else {
        applyFilter();
      }
      return;
    }

    bindListeners();
    updateClearButton();
    ensurePaginationMount();
    bindPaginationListeners();
    const prevCount = cardsCache.length;
    const prevFirst = cardsCache[0];
    refreshCardsCache();
    const cacheChanged = cardsCache.length !== prevCount || cardsCache[0] !== prevFirst;
    if (cacheChanged) {
      if (currentQuery || searchInput?.value) {
        currentQuery = searchInput?.value || currentQuery;
      }
      lastAppliedQuery = null;
      applyFilter();
    }
  };

  const scheduleEnsure = () => {
    window.clearTimeout(ensureTimer);
    ensureTimer = window.setTimeout(ensureSearch, 150);
  };

  const watchProductsContainer = (root) => {
    const container = getProductsContainer(root);
    if (!container || container.dataset.earthHeroProductsWatched === 'true') return;

    let settleTimer = null;
    const observer = new MutationObserver(() => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        if (!isProductsReady(getWidgetRoot())) return;
        reapplyAfterProductsUpdate();
      }, 300);
    });

    observer.observe(container, { childList: true, subtree: true });
    container.dataset.earthHeroProductsWatched = 'true';
  };

  const startWatchers = () => {
    window.setInterval(() => {
      ensureSearch();
      watchProductsContainer(getWidgetRoot());
    }, 2000);

    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.bdlr__btn, .bdlr__modal-add, .bdlr__summary, .bdlr__product-card')) {
          scheduleEnsure();
        }
      },
      true
    );
  };

  const boot = () => {
    bindElements();
    ensureSearch();
    watchProductsContainer(getWidgetRoot());
    startWatchers();

    let attempts = 0;
    const tick = () => {
      attempts += 1;
      ensureSearch();
      if (attempts < 120) window.setTimeout(tick, 500);
    };
    tick();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
